import { SHARED_SECRET, WEB_APP_URL } from '../config';

// ==================== FRONTEND CACHE =========================

interface CacheEntry<T> { data: T; expiresAt: number }
const _cache = new Map<string, CacheEntry<any>>();

function cacheGet<T>(key: string): T | null {
  const entry = _cache.get(key);
  if (!entry || Date.now() > entry.expiresAt) { _cache.delete(key); return null; }
  return entry.data as T;
}

function cachePut<T>(key: string, data: T, ttlMs: number) {
  _cache.set(key, { data, expiresAt: Date.now() + ttlMs });
}

export function clearTrackerCache(...keys: string[]) {
  if (keys.length === 0) { _cache.clear(); return; }
  keys.forEach(k => _cache.delete(k));
}

// ==================== TYPES ==================================

export interface MentorInfo {
  mentorId: string;
  mentorName: string;
  status?: string;
  phone?: string;
  role?: string;           // 'Senior' | 'Standard'
  seniorMentorId?: string;
}

export interface JuniorStudent {
  studentId: string;
  name: string;
  course: string;
  phase: string;
  totalClasses: number;
  lastClass: {
    date: string;
    attendance: string;
    completionStatus: string;
    topic: string;
  } | null;
}

export interface JuniorMentor {
  mentorId: string;
  mentorName: string;
  phone: string;
  students: JuniorStudent[];
}

export interface StudentBasic {
  studentId: string;
  name: string;
  course: string;
}

export interface StudentFull extends StudentBasic {
  mentorId: string;
  status: string;
  dateJoined: string;
  tabName: string;
  notes: string;
  email: string;
  phone: string;
  salesPerson: string;
}

export interface SalesPersonInfo {
  salesPersonId: string;
  name: string;
  phone: string;
  email: string;
  status: string;
  joiningDate: string;
  birthday: string;
  // Sales CRM
  role?: 'Salesperson' | 'Lead Manager' | 'Sales Admin';
  takesLeads?: boolean;
  crmPaused?: boolean;
  passcode?: string;   // the admin only
}

export interface SpTier {
  name: string; pct: number; next: string | null; needed: number;
  thresholds?: { bronze: number; silver: number; gold: number; diamond: number; diplomat: number };
}
export interface SpPayment {
  id: string; studentName: string; course: string;
  totalPaid: number; courseFee: number; enrollDate: string;
  verificationStatus: string;
  isRunaway: boolean; isFullyPaid: boolean; isPartial: boolean;
  incentiveBase: number; incentive: number;
  incentivePaid: boolean; incentivePaidAt: string | null;
}
export interface SpWeekData {
  weekKey: string; monthKey: string;
  weekLabel: string; weekRange: string; isCurrentWeek: boolean;
  salesCount: number; runaways: number; pendingCount: number;
  earnedAmount: number; pendingAmount: number;
  tier: SpTier; incentive: number; pendingIncentive: number;
  payments: SpPayment[];
}

export interface ClassLogRow {
  timestamp: string;
  studentId: string;
  studentName: string;
  assignedMentor: string;
  takenBy: string;
  date: string;
  day: string;
  attendance: 'Present' | 'Absent';
  portion: string;
  topic: string;
  startTime: string;
  endTime: string;
  minutes: number;
  completionStatus: 'Task Given' | 'Completed' | 'Not Completed';
  remark: string;
}

export interface DashboardData {
  rows: ClassLogRow[];
  roster: Array<{ studentId: string; name: string; mentorId: string; course: string; status: string }>;
  mentors: MentorInfo[];
}

// ==================== CORE POST ==============================

async function post<T>(action: string, payload: Record<string, unknown> = {}): Promise<T> {
  // Coordinator sessions encode as "COORD:<mentorId>" — translate to coordinatorId for server
  const finalPayload = { ...payload };
  if (typeof finalPayload.adminPassword === 'string' && (finalPayload.adminPassword as string).startsWith('COORD:')) {
    finalPayload.coordinatorId = (finalPayload.adminPassword as string).slice(6);
    delete finalPayload.adminPassword;
  }
  const body = JSON.stringify({ action, secret: SHARED_SECRET, ...finalPayload });

  let resp: Response;
  try {
    resp = await fetch(WEB_APP_URL, {
      method: 'POST',
      headers: { 'Content-Type': 'text/plain;charset=utf-8' },
      body,
    });
  } catch (err) {
    throw new Error('Network error — check your connection or Web App URL');
  }

  let data: any;
  try {
    data = await resp.json();
  } catch {
    throw new Error('Server returned non-JSON response');
  }

  if (!data.ok) {
    throw new Error(data.error || 'Server returned an error');
  }

  return data as T;
}

// ==================== MENTOR API =============================

// coordToken: a Student Coordinator's sign-in for the sales meetings;
// csToken: a Career Strategist's, for the P2P students
export async function mentorLogin(passcode: string): Promise<{ ok: true; mentorId: string; mentorName: string; role: string; seniorMentorId: string; isPlacementCoordinator?: boolean; coordToken?: string; isCareerStrategist?: boolean; csToken?: string }> {
  return post('mentorLogin', { passcode });
}

// ── Path to Placement: the Career Strategists' students (server/routes/p2p.js) ──
// Each student who paid the test fee, with where they are now (the shape of
// the CRM's P2P card, crm/crmApi.ts P2PCase) and why they look stuck
export async function p2pStudents(csToken: string, tab: 'active' | 'placement' | 'all'): Promise<{ ok: true; tab: string; counts: { active: number; placement: number; all: number; stuck: number }; students: import('../crm/crmApi').P2PCase[] }> {
  return post('p2pStudents', { csToken, tab });
}
export async function p2pStudentResume(csToken: string, caseId: string): Promise<{ ok: true; url: string; fileName: string }> {
  return post('p2pStudentResume', { csToken, caseId });
}
export async function setCareerStrategist(adminPassword: string, mentorId: string, isCareerStrategist: boolean): Promise<{ ok: true }> {
  return withAdmin('setCareerStrategist', adminPassword, { mentorId, isCareerStrategist });
}

export async function promoteMentor(adminPassword: string, mentorId: string, role: string, seniorMentorId?: string): Promise<{ ok: true }> {
  return withAdmin('promoteMentor', adminPassword, { mentorId, role, seniorMentorId: seniorMentorId || '' });
}

export async function getJuniorMentorOverview(seniorMentorId: string): Promise<{ ok: true; juniors: JuniorMentor[] }> {
  return post('getJuniorMentorOverview', { seniorMentorId });
}

export async function getMentorStudents(mentorId: string): Promise<{ ok: true; students: StudentBasic[] }> {
  const key = `getMentorStudents:${mentorId}`;
  const hit = cacheGet<{ ok: true; students: StudentBasic[] }>(key);
  if (hit) return hit;
  const result = await post<{ ok: true; students: StudentBasic[] }>('getMentorStudents', { mentorId });
  cachePut(key, result, 60_000);
  return result;
}

export interface SubmitClassPayload {
  studentId: string;
  takenByMentorId: string;
  date: string;
  attendance: 'Present' | 'Absent';
  absentType?: 'Informed' | 'NotInformed' | 'Leave';
  sendAbsentEmail?: boolean;
  portion: string;
  topic: string;
  startTime: string;
  endTime: string;
  completionStatus: 'Task Given' | 'Completed' | 'Not Completed';
  remark: string;
  tasksGiven?: number;
  tasksCompleted?: number;
  // The tasks typed in the class (each goes to the student; [] = none given)
  tasks?: TaskItem[];
}

export async function submitClass(payload: SubmitClassPayload): Promise<{ ok: true }> {
  return post('submitClass', payload as unknown as Record<string, unknown>);
}

export async function getMentors(): Promise<{ ok: true; mentors: MentorInfo[] }> {
  const key = 'getMentors';
  const hit = cacheGet<{ ok: true; mentors: MentorInfo[] }>(key);
  if (hit) return hit;
  const result = await post<{ ok: true; mentors: MentorInfo[] }>('getMentors');
  cachePut(key, result, 120_000);
  return result;
}

export interface StudentProfile {
  studentId: string;
  name: string;
  course: string;
  dateJoined: string;
  email: string;
  matched: boolean;        // true = found in enrollment sheet by email
  phase: string;
  phone: string;
  college: string;
  degree: string;
  yearOfPassing: string;
  enrollCourse: string;
  sessionCategory: string;
  paymentType: string;
}

export type CoursePhase = 'First 5 Days' | 'Mid Course' | 'Last 10 Days';

export interface PhaseStudent {
  studentId: string;
  name: string;
  mentorId: string;
  mentorName: string;
  course: string;
  dateJoined: string;
  phase: CoursePhase;
  classCount: number;
}

export async function updateStudentPhase(
  mentorId: string,
  studentId: string,
  phase: CoursePhase
): Promise<{ ok: true }> {
  return post('updateStudentPhase', { mentorId, studentId, phase });
}

export async function adminUpdateStudentPhase(
  adminPassword: string,
  studentId: string,
  phase: CoursePhase
): Promise<{ ok: true }> {
  return withAdmin('updateStudentPhase', adminPassword, { studentId, phase });
}

export async function getPhaseData(adminPassword: string): Promise<{ ok: true; students: PhaseStudent[] }> {
  return withAdmin('getPhaseData', adminPassword);
}

export interface ClassRecord {
  date: string;
  day: string;
  takenBy: string;
  attendance: 'Present' | 'Absent' | string;
  portion: string;
  topic: string;
  completionStatus: string;
  remark: string;
}

export async function getMentorStudentProfiles(mentorId: string): Promise<{ ok: true; profiles: StudentProfile[] }> {
  const key = `getMentorStudentProfiles:${mentorId}`;
  const hit = cacheGet<{ ok: true; profiles: StudentProfile[] }>(key);
  if (hit) return hit;
  const result = await post<{ ok: true; profiles: StudentProfile[] }>('getMentorStudentProfiles', { mentorId });
  cachePut(key, result, 60_000);
  return result;
}

export async function getStudentProgress(studentId: string, mentorId: string): Promise<{ ok: true; studentId: string; records: ClassRecord[] }> {
  return post('getStudentProgress', { studentId, mentorId });
}

export interface TimetableSlot {
  mentorId: string;
  studentId: string;
  studentName: string;
  day: string;
  slotStart: string; // "09:00" 24h
}

export async function getTimetableSlots(mentorId: string): Promise<{ ok: true; slots: TimetableSlot[] }> {
  return post('getTimetableSlots', { mentorId });
}

export async function adminSetSlot(
  adminPassword: string,
  mentorId: string,
  studentId: string,
  studentName: string,
  days: string[],
  slotStart: string,
  action: 'add' | 'remove'
): Promise<{ ok: true }> {
  return withAdmin('adminSetSlot', adminPassword, { mentorId, studentId, studentName, days, slotStart, slotAction: action });
}

export interface RecentClassRow {
  logRowIndex: string;
  studentId: string;
  studentName: string;
  date: string;
  attendance: 'Present' | 'Absent';
  topic: string;
  portion: string;
  startTime?: string;
  endTime?: string;
  remark?: string;
  completionStatus: 'Task Given' | 'Completed' | 'Not Completed';
  tasksGiven?: number;
  tasksCompleted?: number;
  assignedMentor?: string;
  takenBy?: string;
  // Latest edit / delete request raised on this class (null if never edited)
  editRequest?: {
    type?: ClassLogRequestType;   // missing on older servers → 'edit'
    status: ClassLogEditStatus;
    changes: ClassLogEditChanges;
    requestedByName: string;
    reviewNote: string;
  } | null;
}

// ==================== CLASS LOG EDIT REQUESTS =================
// Mentors / coordinators can't edit a logged class directly — the change is
// raised as a request and only applied to the log once an admin approves it.

export type ClassLogEditStatus = 'Pending' | 'Approved' | 'Rejected';
export type ClassLogRequestType = 'edit' | 'delete';

export interface ClassLogEditChanges {
  date?: string;
  attendance?: 'Present' | 'Absent';
  startTime?: string;
  endTime?: string;
  portion?: string;
  topic?: string;
  remark?: string;
}

export interface ClassLogEditRequest {
  _id: string;
  type?: ClassLogRequestType;        // missing on requests made before delete existed → 'edit'
  classLogId: string;
  studentId: string;
  studentName: string;
  classDate: string;
  requestedBy: string;
  requestedByName: string;
  requestedByRole: string;
  original: Record<string, string>;
  changes: ClassLogEditChanges;
  reason: string;
  status: ClassLogEditStatus;
  reviewNote: string;
  reviewedAt: string | null;
  createdAt: string;
}

export async function requestClassLogEdit(
  mentorId: string, logRowIndex: string, changes: ClassLogEditChanges, reason: string
): Promise<{ ok: true; revised: boolean }> {
  return post('requestClassLogEdit', { mentorId, logRowIndex, changes, reason });
}

// Asks admin to delete a class log; it stays until approved.
export async function requestClassLogDelete(
  mentorId: string, logRowIndex: string, reason: string
): Promise<{ ok: true; revised: boolean }> {
  return post('requestClassLogDelete', { mentorId, logRowIndex, reason });
}

export async function getClassLogEditRequests(
  adminPassword: string, status: ClassLogEditStatus | 'All', type: ClassLogRequestType | 'All' = 'All'
): Promise<{ ok: true; requests: ClassLogEditRequest[]; pendingCount: number }> {
  return withAdmin('getClassLogEditRequests', adminPassword, { status, type });
}

export async function reviewClassLogEditRequest(
  adminPassword: string, requestId: string, decision: 'Approved' | 'Rejected', note: string
): Promise<{ ok: true }> {
  return withAdmin('reviewClassLogEditRequest', adminPassword, { requestId, decision, note });
}

export async function getRecentClasses(mentorId: string, limit = 100): Promise<{ ok: true; rows: RecentClassRow[] }> {
  return post('getRecentClasses', { mentorId, limit });
}

export async function updateClassStatus(
  mentorId: string,
  logRowIndex: string,
  studentId: string,
  date: string,
  completionStatus: 'Task Given' | 'Completed' | 'Not Completed',
  tasksCompleted?: number
): Promise<{ ok: true }> {
  return post('updateClassStatus', { mentorId, logRowIndex, studentId, date, completionStatus, tasksCompleted });
}

// ==================== ADMIN API ==============================

export async function adminLogin(adminPassword: string): Promise<{ ok: true }> {
  return post('adminLogin', { adminPassword });
}

function withAdmin<T>(action: string, adminPassword: string, payload: Record<string, unknown> = {}): Promise<T> {
  // Coordinator sessions encode their mentorId as 'COORD:<mentorId>'
  if (adminPassword.startsWith('COORD:')) {
    return post<T>(action, { coordinatorId: adminPassword.slice(6), ...payload });
  }
  return post<T>(action, { adminPassword, ...payload });
}

export async function getStudents(adminPassword: string): Promise<{ ok: true; students: StudentFull[] }> {
  return withAdmin('getStudents', adminPassword);
}

// The pick lists leave out Career Strategists (they take no classes); Manage Mentors asks for everyone
export async function getAdminMentors(adminPassword: string, withStrategists = false): Promise<{ ok: true; mentors: Array<MentorInfo & { passcode: string }> }> {
  return withAdmin('getAdminMentors', adminPassword, { withStrategists });
}

export async function addStudent(
  adminPassword: string,
  name: string,
  course: string,
  mentorId: string,
  notes?: string,
  email?: string,
  sendWelcome?: boolean,
  sessionType?: 'one-to-one' | 'group',
  salesPerson?: string,
  regPaid?: number,
  courseFee?: number,
  phone?: string,
  demoTime?: string,
): Promise<{ ok: true; studentId: string } | { ok: false; duplicate: true; existingName: string; existingId: string; message: string }> {
  return withAdmin('addStudent', adminPassword, { name, course, mentorId, notes: notes || '', email: email || '', phone: phone || '', sendWelcome: sendWelcome ?? false, sessionType: sessionType || 'group', salesPerson: salesPerson || '', regPaid: regPaid ?? 1000, courseFee: courseFee ?? 0, demoTime: demoTime || '' });
}

export async function addStudentForce(
  adminPassword: string,
  name: string, course: string, mentorId: string, notes: string,
  email?: string, sendWelcome?: boolean, sessionType?: string,
  salesPerson?: string, regPaid?: number, courseFee?: number, phone?: string
): Promise<{ ok: true; studentId: string }> {
  return withAdmin('addStudent', adminPassword, { name, course, mentorId, notes: notes || '', email: email || '', phone: phone || '', sendWelcome: sendWelcome ?? false, sessionType: sessionType || 'group', salesPerson: salesPerson || '', regPaid: regPaid ?? 1000, courseFee: courseFee ?? 0, forceCreate: true });
}

export async function updateStudentEmail(
  adminPassword: string,
  studentId: string,
  email: string
): Promise<{ ok: true }> {
  return withAdmin('updateStudentEmail', adminPassword, { studentId, email });
}

export async function updateStudentPhone(
  adminPassword: string,
  studentId: string,
  phone: string
): Promise<{ ok: true }> {
  return withAdmin('updateStudentPhone', adminPassword, { studentId, phone });
}

// Mentor-facing: update contact info for their own assigned student
export async function updateStudentEmailByMentor(mentorId: string, studentId: string, email: string): Promise<{ ok: true }> {
  return post('updateStudentEmail', { mentorId, studentId, email });
}

export async function updateStudentPhoneByMentor(mentorId: string, studentId: string, phone: string): Promise<{ ok: true }> {
  return post('updateStudentPhone', { mentorId, studentId, phone });
}

export async function updateStudentCourse(adminPassword: string, studentId: string, course: string): Promise<{ ok: true }> {
  return withAdmin('updateStudentCourse', adminPassword, { studentId, course });
}

export async function updateStudentCourseByMentor(mentorId: string, studentId: string, course: string): Promise<{ ok: true }> {
  return post('updateStudentCourse', { mentorId, studentId, course });
}

export const COURSE_OPTIONS = [
  { key: 'AIML',        label: 'AI & Machine Learning (AIML)' },
  { key: 'BA_DA',       label: 'Business Analytics + Data Analytics (BA + DA)' },
  { key: 'Data_Science', label: 'Data Science' },
  { key: 'BA_30',       label: 'Business Analytics (30-Day Plan)' },
  { key: 'DA',          label: 'Data Analytics' },
  { key: 'Python_FS',   label: 'Python Full Stack Development' },
];

export async function addMentor(
  adminPassword: string,
  name: string,
  passcode: string,
  phone?: string
): Promise<{ ok: true; mentorId: string }> {
  return withAdmin('addMentor', adminPassword, { name, passcode, phone: phone || '' });
}

export async function removeStudent(
  adminPassword: string,
  studentId: string,
  deactivateReason: 'ranAway' | 'courseFinished' = 'courseFinished'
): Promise<{ ok: true }> {
  return withAdmin('removeStudent', adminPassword, { studentId, deactivateReason });
}

export async function getSalespeople(adminPassword: string): Promise<{ ok: true; salespeople: SalesPersonInfo[] }> {
  return withAdmin('getSalespeople', adminPassword);
}

export async function addSalesperson(
  adminPassword: string,
  data: { name: string; passcode: string; email?: string; phone?: string; joiningDate?: string; role?: string; takesLeads?: boolean }
): Promise<{ ok: true; salesPersonId: string }> {
  return withAdmin('addSalesperson', adminPassword, data);
}

export async function removeSalesperson(adminPassword: string, salesPersonId: string): Promise<{ ok: true; released?: number }> {
  return withAdmin('removeSalesperson', adminPassword, { salesPersonId });
}

export async function reactivateSalesPerson(adminPassword: string, salesPersonId: string): Promise<{ ok: true }> {
  return withAdmin('reactivateSalesPerson', adminPassword, { salesPersonId });
}

export async function updateSalespersonDetails(
  adminPassword: string,
  salesPersonId: string,
  data: { email?: string; phone?: string; role?: string; takesLeads?: boolean }
): Promise<{ ok: true }> {
  return withAdmin('updateSalespersonDetails', adminPassword, { salesPersonId, ...data });
}

export async function salesPersonLogin(passcode: string): Promise<{
  ok: boolean; salesPersonId?: string; name?: string; email?: string; error?: string;
  crmToken?: string; salesRole?: 'Salesperson' | 'Lead Manager' | 'Sales Admin'; takesLeads?: boolean;
}> {
  return post('salesPersonLogin', { passcode });
}

export async function getSalesPersonDashboard(salesPersonId: string): Promise<{
  ok: true;
  salesPerson: { salesPersonId: string; name: string; email: string; phone: string };
  currentWeek: SpWeekData;
  weeks: SpWeekData[];
  allTimeSales: number;
  allTimeEarned: number;
  allTimePending: number;
  allTimeRunaways: number;
  allTimeGiven: number;
  allTimeOutstanding: number;
}> {
  return post('getSalesPersonDashboard', { salesPersonId });
}

export interface IncentivePaymentRow {
  id: string; studentName: string; course: string;
  totalPaid: number; courseFee: number; enrollDate: string;
  weekLabel: string; weekRange: string;
  tierName: string; tierPct: number;
  incentive: number; incentivePaid: boolean; incentivePaidAt: string | null;
}
export interface IncentiveSpRow {
  salesPerson: string;
  totalEarned: number; totalPaid: number; outstanding: number;
  payments: IncentivePaymentRow[];
}

export async function getIncentiveMonitor(adminPassword: string): Promise<{
  ok: true; data: IncentiveSpRow[];
}> {
  return withAdmin('getIncentiveMonitor', adminPassword);
}

export async function markIncentivePaid(
  adminPassword: string, paymentId: string, undo?: boolean
): Promise<{ ok: true; incentivePaid: boolean }> {
  return withAdmin('markIncentivePaid', adminPassword, { paymentId, undo: undo ?? false });
}

export interface TierThresholds {
  bronze?: number;
  silver?: number;
  gold?: number;
  diamond?: number;
  diplomat?: number;
}

export interface SalesGoalData {
  salesPersonId: string | null;
  weekKey: string | null;
  weeklyTarget: number;
  headline: string;
  message: string;
  badge: string;
  emoji: string;
  themeColor: string;
  challenge?: string;
  reward?: string;
  difficulty?: 'Easy' | 'Standard' | 'Advanced' | 'Extreme';
  tierThresholds?: TierThresholds;
  updatedAt?: string;
}

export interface SalesQuoteData {
  _id: string;
  quote: string;
  author: string;
  active: boolean;
  createdAt: string;
}

export async function setSalesGoal(
  adminPassword: string,
  goal: Omit<SalesGoalData, 'updatedAt'>
): Promise<{ ok: true }> {
  return withAdmin('setSalesGoal', adminPassword, goal);
}

export async function getSalesGoals(
  adminPassword: string
): Promise<{ ok: true; goals: SalesGoalData[] }> {
  return withAdmin('getSalesGoals', adminPassword);
}

export async function deleteSalesGoal(
  adminPassword: string,
  salesPersonId: string | null,
  weekKey: string
): Promise<{ ok: true }> {
  return withAdmin('deleteSalesGoal', adminPassword, { salesPersonId, weekKey });
}

export interface GoalMonitorEntry {
  salesPersonId: string; name: string;
  sales: number; target: number; pct: number;
  tier: SpTier; badge: string; difficulty: string;
  challenge: string; reward: string;
  themeColor: string; emoji: string; hasGoal: boolean;
  status?: string; headline?: string;
  // The week the goal was set for — earlier than the week shown when it carried over
  goalWeekKey?: string | null; carriedOver?: boolean;
}
export interface GoalMonitorWeek {
  ok: true; weekKey: string; weekLabel: string;
  currentWeekKey: string; prevWeekKey: string; nextWeekKey: string;
  status: 'past' | 'current' | 'upcoming';
  monitor: GoalMonitorEntry[];
}
// One week's goal progress — this week unless weekKey (that week's Monday) is given
export async function getGoalMonitor(adminPassword: string, weekKey?: string): Promise<GoalMonitorWeek> {
  return withAdmin('getGoalMonitor', adminPassword, weekKey ? { weekKey } : {});
}

export async function getSpMotivation(
  salesPersonId: string
): Promise<{ ok: true; goal: SalesGoalData | null; todayQuote: { quote: string; author: string } | null; currentWeekKey: string }> {
  return post('getSpMotivation', { salesPersonId });
}

export interface EodSubmitPayload {
  salesPersonId: string;
  callsMade: number;
  demosGiven: number;
  conversions: number;
  mood: string;
  highlights: string;
  challenges: string;
  plan: string;
}

export async function submitEOD(payload: EodSubmitPayload): Promise<{ ok: true; updated: boolean }> {
  return post('submitEOD', payload as unknown as Record<string, unknown>);
}

// The day's calls from the sales CRM (the EOD numbers come from these).
// pitched: leads sent a brochure; interested / notInterested / noResponse: poll statuses
export interface EodCounts {
  calls: number; talked: number; notPicked: number; callLater: number; cold: number;
  pitched: number; pollsSent?: number; interested?: number; noResponse: number; notInterested: number; converted: number;
}

export interface EodReportItem {
  salesPersonId: string;
  salesPersonName: string;
  date: string;
  callsMade: number;
  demosGiven: number;
  conversions: number;
  highlights: string;
  challenges: string;
  plan: string;
  mood: string;
  submittedAt: string;
  crm?: EodCounts | null;
  auto?: boolean;   // made at the end of the day: they didn't send one
}

// The EOD form: today's numbers so far, and the report if one went already
export async function getMyEodToday(salesPersonId: string): Promise<{ ok: true; date: string; counts: EodCounts; report: EodReportItem | null }> {
  return post('getMyEodToday', { salesPersonId });
}

export async function getEODReports(filters?: { date?: string }): Promise<{ ok: true; reports: EodReportItem[] }> {
  return post('getEODReports', filters || {});
}

export async function deleteEODReport(reportId: string): Promise<{ ok: true }> {
  return post('deleteEODReport', { reportId });
}

export async function addSalesQuote(
  adminPassword: string, quote: string, author?: string
): Promise<{ ok: true }> {
  return withAdmin('addSalesQuote', adminPassword, { quote, author: author || '' });
}

export async function getSalesQuotes(
  adminPassword: string
): Promise<{ ok: true; quotes: SalesQuoteData[] }> {
  return withAdmin('getSalesQuotes', adminPassword);
}

export async function deleteSalesQuote(
  adminPassword: string, quoteId: string
): Promise<{ ok: true }> {
  return withAdmin('deleteSalesQuote', adminPassword, { quoteId });
}

export async function toggleSalesQuote(
  adminPassword: string, quoteId: string
): Promise<{ ok: true; active: boolean }> {
  return withAdmin('toggleSalesQuote', adminPassword, { quoteId });
}

export async function reactivateStudent(
  adminPassword: string,
  studentId: string,
  mentorId: string
): Promise<{ ok: true }> {
  return withAdmin('reactivateStudent', adminPassword, { studentId, mentorId });
}

export async function reassignStudent(
  adminPassword: string,
  studentId: string,
  newMentorId: string
): Promise<{ ok: true }> {
  return withAdmin('reassignStudent', adminPassword, { studentId, newMentorId });
}

// ==================== PAYMENT TRACKER API ====================

export interface PaymentRecord {
  rowIndex: string;
  enrollDate: string;
  name: string;
  email: string;
  phone: string;
  college: string;
  course: string;
  sessionCategory: string;
  sessionType: string;
  courseFee: number;
  regPaid: number;
  balancePaid: number;
  totalPaid: number;
  balance: number;
  status: 'Fully Paid' | 'Partial' | 'Ran Away' | 'Class Over';
  verificationStatus: string;
  salesPerson: string;
  classCount: number;
  incentivePaid?: boolean;
  // The open next-payment-date request, if any (see PAYMENT DATES below)
  nextPayment?: NextPayment | null;
  // From the latest server (older servers leave these out):
  mode?: 'online' | 'offline';
  joined?: string;            // joining date as YYYY-MM-DD
  classesAttended?: number;   // classes marked Present
  studentId?: string;         // roster student — '' when not on the roster yet
  studentStatus?: string;
  studentEmail?: string;
  mentorId?: string;
  mentorName?: string;
}

// `today` is the server's date in IST (YYYY-MM-DD), for comparing due dates.
export async function getPaymentData(adminPassword: string): Promise<{ ok: true; today?: string; records: PaymentRecord[] }> {
  return withAdmin('getPaymentData', adminPassword);
}

export async function addPaymentRecord(
  adminPassword: string,
  record: {
    name: string;
    email: string;
    phone: string;
    course: string;
    sessionType: 'one-to-one' | 'group';
    regPaid: number;
    balancePaid: number;
    enrollDate: string;
  }
): Promise<{ ok: true }> {
  return withAdmin('addPaymentRecord', adminPassword, record as unknown as Record<string, unknown>);
}

export async function updatePaymentRecord(
  adminPassword: string,
  rowIndex: string,
  balancePaid: number,
  sessionType?: string,
  salesPerson?: string,
  courseFee?: number,
  regPaid?: number,
  enrollDate?: string,
): Promise<{ ok: true; newTotal: number; sessionType: string }> {
  return withAdmin('updatePaymentRecord', adminPassword, { rowIndex, balancePaid, sessionType, salesPerson, courseFee, regPaid, enrollDate });
}

export async function updateCourseFee(adminPassword: string, rowIndex: string, courseFee: number): Promise<{ ok: true }> {
  return withAdmin('updateCourseFee', adminPassword, { rowIndex, courseFee });
}

export async function markRunAway(
  adminPassword: string,
  rowIndex: string,
  flag: boolean
): Promise<{ ok: true }> {
  return withAdmin('markRunAway', adminPassword, { rowIndex, flag });
}

export async function setPaymentClassOver(
  adminPassword: string,
  rowIndex: string,
  undo?: boolean
): Promise<{ ok: true }> {
  return withAdmin('setPaymentClassOver', adminPassword, { rowIndex, undo });
}

export async function deduplicatePayments(adminPassword: string): Promise<{ ok: true; removed: number }> {
  return withAdmin('deduplicatePayments', adminPassword);
}

export async function deletePaymentRecord(adminPassword: string, rowIndex: number): Promise<{ ok: true }> {
  return withAdmin('deletePaymentRecord', adminPassword, { rowIndex });
}

export interface UnassignedLead {
  _id: string;
  enrollDate: string;
  name: string;
  email: string;
  phone: string;
  course: string;
  sessionType: string;
  regPaid: number;
  balancePaid?: number;
  totalPaid: number;
  salesPerson: string;
  courseFee: number;
  // Pay After Placement group-session extras (set when the lead comes from
  // /pay-after-placement-group)
  collegeName?: string;
  degree?: string;
  yearOfPassout?: string;
  sessionCategory?: string;
  slotPreference?: string;
  // legacy fields kept for compatibility
  college?: string;
  yearOfPassing?: string;
  paymentType?: string;
  dateRegistered?: string;
}

export async function getUnassignedLeads(adminPassword: string): Promise<{ ok: true; leads: UnassignedLead[] }> {
  return withAdmin('getUnassignedLeads', adminPassword);
}

export async function getSeniorLeads(mentorId: string): Promise<{ ok: true; leads: UnassignedLead[] }> {
  return post('getSeniorLeads', { mentorId });
}

// ==================== SESSION SLOTS ===========================
// Admin-configurable time slots offered on the /pay-after-placement-group form.
// Each course has its own slots.

// The group form's courses, as it stores them on the payment record
export const SLOT_COURSES = [
  { value: 'full stack analytics', label: 'Full Stack Analytics' },
  { value: 'AIML', label: 'AIML' },
] as const;
export type SlotCourse = typeof SLOT_COURSES[number]['value'];

export interface SessionSlot {
  _id: string;
  label: string;
  course?: SlotCourse | '';   // '' / missing: made before slots were per course — shown for every course
  sessionCategory: 'online' | 'offline' | 'both';
  available: boolean;
  order: number;
}

export async function getSessionSlots(adminPassword: string): Promise<{ ok: true; slots: SessionSlot[] }> {
  return withAdmin('getSessionSlots', adminPassword);
}

export async function upsertSessionSlot(adminPassword: string, slot: {
  slotId?: string; label: string; course: SlotCourse | ''; sessionCategory: SessionSlot['sessionCategory']; available: boolean; order: number;
}): Promise<{ ok: true }> {
  return withAdmin('upsertSessionSlot', adminPassword, slot);
}

export async function deleteSessionSlot(adminPassword: string, slotId: string): Promise<{ ok: true }> {
  return withAdmin('deleteSessionSlot', adminPassword, { slotId });
}

// ==================== PAYSLIPS ================================
// Admin fills in a month's salary details; the server renders the PDF payslip
// and emails it to the mentor / salesperson.

export type PayslipEmployeeType = 'mentor' | 'sales';

export interface PayslipEmployee {
  employeeType: PayslipEmployeeType;
  employeeId: string;
  name: string;
  email: string;
  status: string;
  // Leave in the month getPayslipEmployees was asked for
  leaveDays: number;                              // approved leave days
  leaveDates: { date: string; days: number }[];   // days is 0.5 for a half day
  pendingLeaveRequests: number;                   // not yet approved / rejected
  // A salesperson's office attendance in the month so far (Start work / End
  // work in the Sales CRM); missing while attendance is off or for mentors
  attendance?: {
    present: number; late: number; lateMinutes: number; early: number; noEnd: number;
    absent: number; absentDates: { date: string; label: string; days: number }[];
  };
}

export interface PayslipInput {
  employeeType: PayslipEmployeeType;
  employeeId: string;
  email: string;
  month: string;             // 'YYYY-MM'
  basicDa: number;
  hra: number;
  incentive?: number;        // salespeople only
  totalDays: number;
  leaveDays: number;
  esic: number;
  epfo: number;
  deductLeave: boolean;      // false → leave days are shown but nothing is deducted
  leaveDeduction?: number;   // omitted → server uses per-day fixed salary × leave days
}

export interface PayslipRecord extends Required<PayslipInput> {
  _id: string;
  name: string;
  daysWorked: number;
  grossEarnings: number;
  totalDeductions: number;
  netPay: number;
  sentAt: string;
}

// Active mentors + salespeople, each with their approved leave for `month` ('YYYY-MM').
export async function getPayslipEmployees(adminPassword: string, month: string): Promise<{ ok: true; employees: PayslipEmployee[] }> {
  return withAdmin('getPayslipEmployees', adminPassword, { month });
}

export async function getPayslips(adminPassword: string): Promise<{ ok: true; payslips: PayslipRecord[] }> {
  return withAdmin('getPayslips', adminPassword);
}

export async function previewPayslip(adminPassword: string, input: PayslipInput): Promise<{ ok: true; pdfBase64: string; fileName: string }> {
  return withAdmin('previewPayslip', adminPassword, input as unknown as Record<string, unknown>);
}

export async function sendPayslip(adminPassword: string, input: PayslipInput): Promise<{ ok: true; sentTo: string; payslip: PayslipRecord }> {
  return withAdmin('sendPayslip', adminPassword, input as unknown as Record<string, unknown>);
}

// ==================== LEAVE REQUESTS ==========================
// Mentors, coordinators and salespeople apply for leave; admin approves or
// rejects it. Approved leave fills in Leave Days on the Payslips page.
// Sundays and institute holidays inside a leave don't count.

export type LeaveStatus = 'Pending' | 'Approved' | 'Rejected';

export interface LeaveRequest {
  _id: string;
  employeeType: PayslipEmployeeType;
  employeeId: string;
  name: string;
  role: string;         // mentor role, or 'Sales'
  fromDate: string;     // 'YYYY-MM-DD'
  toDate: string;
  halfDay: boolean;
  fromTime?: string;   // a half day's hours, India time 'HH:MM'
  toTime?: string;
  days: number;
  reason: string;
  status: LeaveStatus;
  reviewNote: string;
  reviewedAt: string | null;
  createdAt: string;
}

export interface LeaveRequestInput {
  employeeType: PayslipEmployeeType;
  employeeId: string;
  fromDate: string;
  toDate: string;
  halfDay: boolean;
  fromTime?: string;
  toTime?: string;
  reason: string;
}

export async function requestLeave(input: LeaveRequestInput): Promise<{ ok: true; request: LeaveRequest }> {
  return post('requestLeave', input as unknown as Record<string, unknown>);
}

export async function getMyLeaves(employeeType: PayslipEmployeeType, employeeId: string): Promise<{ ok: true; requests: LeaveRequest[] }> {
  return post('getMyLeaves', { employeeType, employeeId });
}

// Only while the request is still Pending.
export async function cancelLeaveRequest(employeeType: PayslipEmployeeType, employeeId: string, requestId: string): Promise<{ ok: true }> {
  return post('cancelLeaveRequest', { employeeType, employeeId, requestId });
}

export async function getLeaveRequests(
  adminPassword: string, status: LeaveStatus | 'All'
): Promise<{ ok: true; requests: LeaveRequest[]; pendingCount: number }> {
  return withAdmin('getLeaveRequests', adminPassword, { status });
}

// Approving full-day mentor leave also marks it on the Attendance page
// (attendanceDays). The person is emailed the decision at emailSentTo
// ('' = no email on file).
export async function reviewLeaveRequest(
  adminPassword: string, requestId: string, decision: 'Approved' | 'Rejected', note: string
): Promise<{ ok: true; attendanceDays: number; emailSentTo: string }> {
  return withAdmin('reviewLeaveRequest', adminPassword, { requestId, decision, note });
}

// ==================== PAYMENT DATES ===========================
// A partly-paid student who has attended 5 classes is asked for the date of
// their next payment (Student Portal + email). Admin approves it, or rejects
// it and gives another date; the student is emailed either way. A request
// closes by itself once a payment is recorded after it.
//   Asked     – waiting for the student to pick a date
//   Pending   – student picked requestedDate, waiting for admin
//   Approved  – dueDate = requestedDate
//   Rejected  – admin gave dueDate instead
//   Scheduled – admin set / changed dueDate directly

export type PaymentDateStatus = 'Asked' | 'Pending' | 'Approved' | 'Rejected' | 'Scheduled';
export const DATED_PAYMENT_STATUSES: PaymentDateStatus[] = ['Approved', 'Rejected', 'Scheduled'];

// A student's open request, as the Payments tab shows it
export interface NextPayment {
  requestId: string;
  status: PaymentDateStatus;
  requestedDate: string;
  dueDate: string;
  studentNote?: string;
  askedAt?: string | null;
}

export interface PaymentDateRequest {
  _id: string;
  studentId: string;
  paymentId: string;
  studentName: string;
  email: string;
  phone: string;
  course: string;
  courseFee: number;
  totalPaid: number;        // when asked
  balance: number;          // when asked
  classesAttended: number;
  status: PaymentDateStatus;
  requestedDate: string;    // 'YYYY-MM-DD'
  studentNote: string;
  requestedAt: string | null;
  dueDate: string;          // 'YYYY-MM-DD'
  adminNote: string;
  reviewedAt: string | null;
  askEmailSentAt: string | null;
  open: boolean;
  settledAt: string | null;
  settledReason: string;
  createdAt: string;
  currentTotalPaid: number;
  currentBalance: number;
}

export async function getPaymentDateRequests(adminPassword: string): Promise<{ ok: true; today: string; requests: PaymentDateRequest[] }> {
  return withAdmin('getPaymentDateRequests', adminPassword);
}

// Rejecting needs the date admin gives instead (dueDate). The student is
// emailed at emailSentTo ('' = no email on file).
export async function reviewPaymentDateRequest(
  adminPassword: string, requestId: string, decision: 'Approved' | 'Rejected', dueDate: string, note: string
): Promise<{ ok: true; emailSentTo: string; nextPayment?: NextPayment }> {
  return withAdmin('reviewPaymentDateRequest', adminPassword, { requestId, decision, dueDate, note });
}

// Set the date for a student who hasn't picked one, or move an agreed date.
export async function setPaymentDueDate(
  adminPassword: string, requestId: string, dueDate: string, note: string
): Promise<{ ok: true; emailSentTo: string; nextPayment?: NextPayment }> {
  return withAdmin('setPaymentDueDate', adminPassword, { requestId, dueDate, note });
}

// From the Payments tab, by payment record: sets the date even when the
// student hasn't been asked yet.
export async function setPaymentDateForRecord(
  adminPassword: string, paymentId: string, dueDate: string, note: string
): Promise<{ ok: true; emailSentTo: string; nextPayment: NextPayment }> {
  return withAdmin('setPaymentDueDate', adminPassword, { paymentId, dueDate, note });
}

// Emails the student a link to choose their next payment date (also before
// their 5th class), or a reminder if they were asked and haven't chosen yet.
export async function askForPaymentDate(
  adminPassword: string, paymentId: string
): Promise<{ ok: true; emailSentTo: string; reminder: boolean; nextPayment: NextPayment }> {
  return withAdmin('askForPaymentDate', adminPassword, { paymentId });
}

// ==================== FEE RECEIPTS ============================
// From the Payments page, admin emails a student the PDF receipt for a payment
// they made (UPI, cash…), typing the amount in. record: true also adds the
// amount to their payments; false when it's already in via Update Payment.
// Receipts the website's enrolment / balance-payment forms send are listed too.

export const RECEIPT_METHODS = ['UPI', 'Cash', 'Bank transfer', 'Card', 'Cheque', 'Other'] as const;

export interface ReceiptInput {
  paymentId: string;
  amount: number;
  paidOn: string;       // 'YYYY-MM-DD'
  method: string;       // one of RECEIPT_METHODS, or ''
  reference: string;
  note: string;         // goes in the email
  email: string;        // '' → the student's email on file
  record: boolean;
}

export interface PaymentReceipt {
  _id: string;
  receiptNo: string;
  paymentId: string;
  source: 'admin' | 'website';
  name: string;
  email: string;        // where it was last sent
  amount: number;
  paidOn: string;
  method: string;
  reference: string;
  note: string;
  courseFee: number;
  totalPaid: number;    // after this payment
  balance: number;      // after this payment
  recorded: boolean;    // added to their payments when it was sent
  issuedAt: string | null;
  lastSentAt: string | null;
  sendCount: number;
}

export async function previewPaymentReceipt(adminPassword: string, input: ReceiptInput): Promise<{ ok: true; pdfBase64: string; fileName: string }> {
  return withAdmin('previewPaymentReceipt', adminPassword, input as unknown as Record<string, unknown>);
}

// payment: the record's totals after this (changed only when record was true)
export async function sendPaymentReceipt(adminPassword: string, input: ReceiptInput): Promise<{
  ok: true; sentTo: string; receipt: PaymentReceipt | null;
  payment: { balancePaid: number; totalPaid: number; courseFee: number };
}> {
  return withAdmin('sendPaymentReceipt', adminPassword, input as unknown as Record<string, unknown>);
}

export async function getPaymentReceipts(adminPassword: string, paymentId: string): Promise<{ ok: true; receipts: PaymentReceipt[] }> {
  return withAdmin('getPaymentReceipts', adminPassword, { paymentId });
}

// Same receipt again; email '' → the address it last went to
export async function resendPaymentReceipt(adminPassword: string, receiptId: string, email: string): Promise<{ ok: true; sentTo: string; receipt: PaymentReceipt }> {
  return withAdmin('resendPaymentReceipt', adminPassword, { receiptId, email });
}

// ==================== CLASS OVER ==============================
// Raised by a mentor (own students) or Student Coordinator (any student) when
// a course is finished: the student is deactivated, their payment marked Class
// Over and timetable slots freed. Admin is emailed and can undo it.

export type ClassOverStatus = 'New' | 'Seen' | 'Undone';

export interface ClassOverReport {
  _id: string;
  studentId: string;
  studentName: string;
  course: string;
  email: string;
  phone: string;
  mentorId: string;
  mentorName: string;
  raisedBy: string;
  raisedByName: string;
  raisedByRole: string;     // Standard | Senior | Student Coordinator
  note: string;
  totalClasses: number;
  present: number;
  absent: number;
  firstClass: string;
  lastClass: string;
  courseFee: number;
  totalPaid: number;
  balance: number;
  timetableSlots: { mentorId: string; day: string; slotStart: string }[];
  status: ClassOverStatus;
  seenAt: string | null;
  undoneAt: string | null;
  createdAt: string;
}

export async function raiseClassOver(mentorId: string, studentId: string, note: string): Promise<{ ok: true; reportId: string; studentName: string }> {
  const res = await post<{ ok: true; reportId: string; studentName: string }>('raiseClassOver', { mentorId, studentId, note });
  clearTrackerCache(`getMentorStudentProfiles:${mentorId}`, `getMentorStudents:${mentorId}`);
  return res;
}

export async function getClassOverReports(
  adminPassword: string, status: ClassOverStatus | 'All'
): Promise<{ ok: true; reports: ClassOverReport[]; newCount: number }> {
  return withAdmin('getClassOverReports', adminPassword, { status });
}

// One report, or every New one when reportId is omitted.
export async function markClassOverSeen(adminPassword: string, reportId?: string): Promise<{ ok: true; updated: number }> {
  return withAdmin('markClassOverSeen', adminPassword, reportId ? { reportId } : { all: true });
}

export async function undoClassOver(adminPassword: string, reportId: string): Promise<{ ok: true; studentReactivated: boolean; timetableSlots: number }> {
  return withAdmin('undoClassOver', adminPassword, { reportId });
}

// ==================== ADMIN NOTIFICATIONS =====================
// Worked out from what's waiting, so an item disappears once dealt with.
// Ids are stable — new ids since the last check are new notifications.

export type AdminNotificationType = 'paymentDateRequest' | 'paymentDueToday' | 'paymentOverdue' | 'classOver' | 'resumeReview';
export type AdminNotificationLink = 'payment-dates' | 'payments' | 'class-over' | 'resume-reviews';

export interface AdminNotification {
  id: string;
  type: AdminNotificationType;
  title: string;
  body: string;
  at: string;
  link: AdminNotificationLink;
}

export interface AdminNotificationCounts {
  paymentDateRequests: number;
  paymentsDueToday: number;
  paymentsOverdue: number;
  classOver: number;
  resumeReviews?: number;
  total: number;
}

export async function getAdminNotifications(adminPassword: string): Promise<{
  ok: true; today: string; counts: AdminNotificationCounts; items: AdminNotification[];
}> {
  return withAdmin('getAdminNotifications', adminPassword);
}

// ==================== RESUME REVIEWS ==========================
// Slots booked on the public /resume-review page (3–6 PM, 30 minutes each).
// resumeUrl is a path under the API, signed for a few hours.

export type ResumeReviewStatus = 'Booked' | 'Done' | 'No Show' | 'Cancelled';
export type ResumeReviewFilter = 'Upcoming' | 'Today' | 'Past' | 'All';

export interface ResumeReview {
  _id: string;
  name: string;
  email: string;
  phone: string;
  date: string;
  slot: string;
  dateLabel: string;
  timeLabel: string;
  status: ResumeReviewStatus;
  note: string;
  seen: boolean;
  over: boolean;
  createdAt: string;
  statusUpdatedByName: string;
  statusUpdatedAt: string | null;
  resumeFileName: string;
  resumeSize: number;
  resumeUrl: string;
}

export async function getResumeReviews(adminPassword: string, filter: ResumeReviewFilter): Promise<{
  ok: true; today: string; counts: { upcoming: number; today: number; unseen: number; total: number }; reviews: ResumeReview[];
}> {
  return withAdmin('getResumeReviews', adminPassword, { filter });
}

export async function updateResumeReview(
  adminPassword: string, reviewId: string, change: { status?: ResumeReviewStatus; note?: string }
): Promise<{ ok: true; review: ResumeReview }> {
  return withAdmin('updateResumeReview', adminPassword, { reviewId, ...change });
}

export async function markResumeReviewsSeen(adminPassword: string): Promise<{ ok: true; updated: number }> {
  return withAdmin('markResumeReviewsSeen', adminPassword);
}

export const resumeFileUrl = (path: string) => `${WEB_APP_URL}${path}`;

// ==================== ATS RESUME AI ===========================
// Admin only. Check (the AI reads the resume and the job) → the admin's
// choices → Make (the AI rewrites the wording), or Compare (2–4 models each
// write a version; the admin keeps the best) → Selection Boosters and chat.
// The AI steps run on the server in the background: atsCheck / atsMake /
// atsCompare / atsChat return at once and the page polls atsGet until
// nothing is busy. File links (fileUrl, pdfUrl, docxUrl) are paths under the
// API — open them with resumeFileUrl().

export type AtsVerdict = 'eligible' | 'not_eligible' | 'check';
export type AtsMatch = 'strong' | 'medium' | 'weak';
export type AtsAnswer = 'done' | 'build' | 'skip';
export type AtsSection = 'summary' | 'skills' | 'experience' | 'projects' | 'education' | 'certifications' | 'achievements' | 'extra';
export type AtsDesign = 'classic' | 'modern' | 'compact';
export type AtsStep = 'check' | 'make' | 'chat';
export type AtsModels = Record<AtsStep, string>;   // model id per step

// One step of a model in the Model Lab: real numbers from its runs (the cost
// is a guess until it has 3 good runs)
export interface AtsStepStats { runs: number; ok: number; sec: number | null; inr: number; estimated: boolean; gain: number | null; held: number | null }
export interface AtsModelInfo {
  id: string; name: string; blurb: string;
  price: { input: number; output: number };   // $ per million tokens
  resumeInr: number; estimated: boolean;      // a Check + Make
  check: AtsStepStats; make: AtsStepStats; chat: AtsStepStats;
  compares: number; wins: number;
}

export interface AtsLine { id: string; text: string; original: string }
export interface AtsText { text: string; original: string }
export interface AtsExperience { id: string; role: string; company: string; location: string; start: string; end: string; source: string; bullets: AtsLine[] }
export interface AtsProject { id: string; name: string; tech: string[]; link: string; source: 'resume' | 'suggested' | 'course' | 'chat'; bullets: AtsLine[] }
export interface AtsEducation { degree: string; field: string; institution: string; location: string; start: string; end: string; score: string }
export interface AtsCertification { id: string; name: string; issuer: string; year: string }

export interface AtsResumeDoc {
  name: string;
  headline: AtsText;
  email: string; phone: string; location: string; links: string[];
  summary: AtsText;
  skills: { group: string; items: string[] }[];
  experience: AtsExperience[];
  projects: AtsProject[];
  education: AtsEducation[];
  certifications: AtsCertification[];
  achievements: AtsLine[];
  extra: { heading: string; items: string[] }[];
  sectionOrder: AtsSection[];
}

// The resume as it was uploaded (bullets with ids, no "original")
export interface AtsOriginal {
  name: string; headline: string; email: string; phone: string; location: string; links: string[];
  summary: string;
  skills: { group: string; items: string[] }[];
  experience: (Omit<AtsExperience, 'bullets' | 'source'> & { bullets: { id: string; text: string }[] })[];
  projects: (Omit<AtsProject, 'bullets' | 'source'> & { bullets: { id: string; text: string }[] })[];
  education: AtsEducation[];
  certifications: AtsCertification[];
  achievements: { id: string; text: string }[];
  extra: { heading: string; items: string[] }[];
}

// One line a change writes: where it goes, the line before, the line after
// (in a preview, after may hold {answer} for the tapped answers)
export interface AtsChange { where: string; before: string; after: string }

// A Selection Booster: a ready-made fix, shown one at a time in the chat —
// "If you add …" (pitch), the exact new lines (preview) and the real score
// now → after. 'apply' needs no facts; 'ask' needs a fact only the admin
// knows: they tap answers (options; several when multi). ready: the server
// applies it at once; otherwise the AI does it. ifNo: how the student can
// get it honestly when the answer is No.
export interface AtsBooster {
  id: string;
  impact: 'high' | 'medium' | 'low';
  title: string; pitch: string; why: string;
  kind: 'apply' | 'ask';
  question: string; detailHint: string; instruction: string;
  options: string[]; multi: boolean; ifNo: string;
  ready: boolean; preview: AtsChange[];
  adds: string[];
  gain: number;
  scoreNow: number; scoreAfter: number;   // worked out on the resume as it is now
  step: number; of: number;               // "Suggestion 2 of 5" (step 0 once answered)
  status: 'open' | 'used' | 'dismissed';
}

export interface AtsChatMessage {
  role: 'admin' | 'ai';
  text: string;
  note: string;                // what the fact check held back
  tag?: string;                // a suggestion: '💡 Suggestion · High impact'
  scoreFrom?: number | null;   // the ATS score change this reply made
  scoreTo?: number | null;
  changes?: AtsChange[];       // what this change wrote
  undoId?: string;             // its Undo point (Undo shows while it's the latest)
  undone?: boolean;
  at: string;
}

// The Coach's Score plan: the score now, after "Apply all" (safe), and the
// most it can honestly reach (max); each missing job keyword with what it
// adds and the suggestion that fixes it ('' = none yet: offer "Fix")
export interface AtsPlan {
  goal: number; now: number; safe: number; max: number;
  ready: number; asks: number;
  missing: { term: string; importance: 'must' | 'nice'; gain: number; boosterId: string; covered: boolean }[];
}

export interface AtsListItem {
  _id: string;
  candidateName: string;   // '' until the check has read the resume
  fileName: string;
  jobTitle: string; company: string;
  step: 'check' | 'done';
  busy: '' | 'check' | 'make' | 'chat';
  error: string;
  verdict: AtsVerdict | '';
  scoreBefore: number; scoreAfter: number;
  costInr: number;          // with every compared version
  models: AtsModels;
  design: AtsDesign;
  tailoredFrom: string;     // the resume this was made from, for another job
  createdAt: string; updatedAt: string;
}

// One model's version in a comparison
export interface AtsCompareRun {
  id: string; model: string;
  busy: string; busySince: string | null; error: string; done: boolean;
  scoreAfter: number; gain: number;
  held: number; fixes: string[];   // lines the fact check put back
  sec: number; inr: number;
  resume: AtsResumeDoc | null; hrMessage: string; boosters: string[];
}

export interface AtsRecord extends AtsListItem {
  jobId: string; jd: string;
  compareOf: string;
  fileUrl: string;
  busySince: string | null;
  timing: { check: number; make: number };   // ms
  check: null | {
    original: AtsOriginal;
    eligibility: { verdict: AtsVerdict; summary: string; items: { area: string; job: string; resume: string; status: 'ok' | 'fail' | 'unclear' }[] };
    projectMatch: { id: string; match: AtsMatch; reason: string }[];
    // adds: the job keywords the project would bring in; gain: ATS score it adds
    suggestions: { id: string; title: string; tech: string[]; why: string; bullets: string[]; adds: string[]; gain: number }[];
    overview: string;
  };
  keywords: { term: string; kind: 'tool' | 'skill' | 'soft' | 'qualification'; importance: 'must' | 'nice'; before: boolean; now: boolean }[];
  choices: null | { answers: Record<string, AtsAnswer>; makeAnyway: boolean };
  resume: AtsResumeDoc | null;
  extras: null | {
    hrMessage: string; buildPlans: { id: string; project: string; days: number; tools: string[]; steps: string[] }[]; fixList: string[]; beforeApply?: string[];
    toLearn?: { boosterId: string; title: string; plan: string }[];   // a "No" with an honest way to get it
  };
  boosters: AtsBooster[];
  plan: AtsPlan | null;
  undoId: string;            // the change Undo can take back ('' = none)
  fixes: string[];
  chat: AtsChatMessage[];
  usage: { calls: number; inr: number };
  compare: null | { id: string; models: string[]; winner: string; chosenId: string; at: string; runs: AtsCompareRun[] };
  pdfUrl?: string; docxUrl?: string;
}

// The page can be newer than the server it talks to (and old records lack
// newer fields), so every resume gets the fields the page reads
const atsItem = <T extends AtsListItem>(r: T): T => ({
  ...r,
  fileName: r.fileName ?? '',
  models: r.models ?? { check: '', make: '', chat: '' },
  design: r.design ?? 'classic',
  tailoredFrom: r.tailoredFrom ?? '',
});
const atsRec = (r: AtsRecord): AtsRecord => ({
  ...atsItem(r),
  compareOf: r.compareOf ?? '',
  timing: r.timing ?? { check: 0, make: 0 },
  compare: r.compare ?? null,
  check: r.check && { ...r.check, suggestions: (r.check.suggestions ?? []).map(s => ({ ...s, adds: s.adds ?? [], gain: s.gain ?? 0 })) },
  boosters: (r.boosters ?? []).map(b => ({ ...b, options: b.options ?? [], multi: !!b.multi, ifNo: b.ifNo ?? '', ready: !!b.ready, preview: b.preview ?? [] })),
  plan: r.plan ?? null,
  undoId: r.undoId ?? '',
});
const withRec = async <T extends { resume: AtsRecord }>(p: Promise<T>): Promise<T> => {
  const res = await p;
  return { ...res, resume: atsRec(res.resume) };
};

export interface AtsJobInput { jobTitle: string; company: string; jd: string; jobId?: string }
export interface AtsCheckInput extends AtsJobInput { resume: { data: string; fileName: string } }

export async function atsOptions(adminPassword: string): Promise<{
  ok: true;
  jobs: { _id: string; title: string; company: string; description: string }[];
  models: AtsModelInfo[];
  defaults: AtsModels | null;
}> {
  const res = await withAdmin<{ ok: true; jobs?: { _id: string; title: string; company: string; description: string }[]; models?: AtsModelInfo[]; defaults?: AtsModels }>('atsOptions', adminPassword);
  return { ok: res.ok, jobs: res.jobs ?? [], models: res.models ?? [], defaults: res.defaults ?? null };
}

export async function atsCheck(adminPassword: string, input: AtsCheckInput): Promise<{ ok: true; id: string }> {
  return withAdmin('atsCheck', adminPassword, { ...input });
}

// The same resume for another job: a new resume, checked against that job
export async function atsTailor(adminPassword: string, id: string, job: AtsJobInput): Promise<{ ok: true; id: string }> {
  return withAdmin('atsTailor', adminPassword, { id, ...job });
}

export async function atsMake(adminPassword: string, id: string, answers: Record<string, AtsAnswer>, makeAnyway: boolean): Promise<{ ok: true }> {
  return withAdmin('atsMake', adminPassword, { id, answers, makeAnyway });
}

export async function atsCompare(adminPassword: string, id: string, models: string[], answers: Record<string, AtsAnswer>, makeAnyway: boolean): Promise<{ ok: true; compareId: string }> {
  return withAdmin('atsCompare', adminPassword, { id, models, answers, makeAnyway });
}

// 🏆 One model's version becomes the resume
export async function atsPickBest(adminPassword: string, id: string, copyId: string): Promise<{ ok: true; resume: AtsRecord }> {
  return withRec(withAdmin('atsPickBest', adminPassword, { id, copyId }));
}

export async function atsDesign(adminPassword: string, id: string, design: AtsDesign): Promise<{ ok: true; resume: AtsRecord }> {
  return withRec(withAdmin('atsDesign', adminPassword, { id, design }));
}

export async function atsLab(adminPassword: string): Promise<{
  ok: true; models: AtsModelInfo[]; defaults: AtsModels;
  recent: { id: string; resumeId: string; candidateName: string; jobTitle: string; company: string; models: string[]; winner: string; at: string }[];
}> {
  return withAdmin('atsLab', adminPassword);
}

// A typed message or a quick button (message), or a missing job keyword to
// get a ready fix for (fixTerm) — the AI answers in the background
export async function atsChat(adminPassword: string, id: string, say: { message: string } | { fixTerm: string }): Promise<{ ok: true }> {
  return withAdmin('atsChat', adminPassword, { id, ...say });
}

// One tap on a suggestion: a ready fix comes back applied (resume); one the
// AI has to do comes back without, and the page polls
export async function atsApplyFix(adminPassword: string, id: string, boosterId: string, answers: string[] = [], other = ''): Promise<{ ok: true; resume?: AtsRecord }> {
  const res = await withAdmin<{ ok: true; resume?: AtsRecord }>('atsApplyFix', adminPassword, { id, boosterId, answers, other });
  return res.resume ? { ...res, resume: atsRec(res.resume) } : res;
}

// Every ready fix that needs no answer, in one go
export async function atsApplyAll(adminPassword: string, id: string): Promise<{ ok: true; resume: AtsRecord }> {
  return withRec(withAdmin('atsApplyAll', adminPassword, { id }));
}

// Takes back the latest Coach change (undoId from the record)
export async function atsUndo(adminPassword: string, id: string, undoId: string): Promise<{ ok: true; resume: AtsRecord }> {
  return withRec(withAdmin('atsUndo', adminPassword, { id, undoId }));
}

// "Learned it": a suggestion answered No comes back on top
export async function atsReopenBooster(adminPassword: string, id: string, boosterId: string): Promise<{ ok: true; resume: AtsRecord }> {
  return withRec(withAdmin('atsReopenBooster', adminPassword, { id, boosterId }));
}

export async function atsDismissBooster(adminPassword: string, id: string, boosterId: string): Promise<{ ok: true; resume: AtsRecord }> {
  return withRec(withAdmin('atsDismissBooster', adminPassword, { id, boosterId }));
}

export async function atsEdit(adminPassword: string, id: string, resume: AtsResumeDoc): Promise<{ ok: true; resume: AtsRecord }> {
  return withRec(withAdmin('atsEdit', adminPassword, { id, resume }));
}

export async function atsGet(adminPassword: string, id: string): Promise<{ ok: true; resume: AtsRecord }> {
  return withRec(withAdmin('atsGet', adminPassword, { id }));
}

export async function atsList(adminPassword: string): Promise<{ ok: true; items: AtsListItem[]; month: { resumes: number; inr: number; avgGain: number }; defaults: AtsModels | null }> {
  const res = await withAdmin<{ ok: true; items?: AtsListItem[]; month?: Partial<{ resumes: number; inr: number; avgGain: number }>; defaults?: AtsModels }>('atsList', adminPassword);
  return {
    ok: res.ok,
    items: (res.items ?? []).map(atsItem),
    month: { resumes: 0, inr: 0, avgGain: 0, ...res.month },
    defaults: res.defaults ?? null,
  };
}

export async function atsDelete(adminPassword: string, id: string): Promise<{ ok: true }> {
  return withAdmin('atsDelete', adminPassword, { id });
}

// Public, no auth — same endpoint the group-session payment form reads from.
// Used by the Sales dashboard so reps can tell students which slots are open.
export async function getPublicSessionSlots(): Promise<{ ok: true; slots: SessionSlot[] }> {
  const resp = await fetch(`${WEB_APP_URL}/session-slots`);
  return resp.json();
}

export async function assignLeadToMentor(
  mentorId: string,
  leadId: string,
  juniorMentorId: string,
  sessionType: string,
  courseFee: number,
  regPaid: number,
  sendWelcome: boolean
): Promise<{ ok: true; studentId: string }> {
  return post('assignLeadToMentor', { mentorId, leadId, juniorMentorId, sessionType, courseFee, regPaid, sendWelcome });
}

export async function mentorReassignStudent(
  mentorId: string,
  studentId: string,
  newMentorId: string
): Promise<{ ok: true }> {
  return post('mentorReassignStudent', { mentorId, studentId, newMentorId });
}

export async function mentorSetSlot(
  mentorId: string,
  studentId: string,
  studentName: string,
  days: string[],
  slotStart: string,
  slotAction: 'add' | 'remove'
): Promise<{ ok: true }> {
  return post('mentorSetSlot', { mentorId, studentId, studentName, days, slotStart, slotAction });
}

export async function sendClassReminders(adminPassword: string, day?: string, mentorId?: string): Promise<{ ok: true; sent: number; skipped: number; day: string; message?: string }> {
  const extra: Record<string, unknown> = {};
  if (day) extra.day = day;
  if (mentorId) extra.mentorId = mentorId;
  return withAdmin('sendClassReminders', adminPassword, extra);
}

// ── DASHBOARD / REPORTS ──────────────────────────────────────────────────────
// Dashboard, Mentor Performance and Revenue share one set of definitions (see
// server/routes/reports.js). A period is from / to — YYYY-MM-DD, IST,
// inclusive, from '' for all time — plus an optional period to compare with.

export interface ReportPeriod { from: string; to: string; prevFrom?: string; prevTo?: string }
export interface ReportRange { from: string; to: string; today: string; prevFrom: string; prevTo: string }
export interface MentorOption { mentorId: string; name: string; role: string; status: string }

export interface ClassTally {
  held: number; noShows: number; minutes: number; attPct: number | null;
  tasksDone: number; tasksMissed: number; tasksPending: number;
}

export interface ClassSeriesPoint { key: string; label: string; from: string; to: string; held: number; noShows: number }

export interface FeeTotals {
  students: number; collected: number; outstanding: number; lost: number; total: number; regCollected: number;
  fullyPaid: number; owing: number; lostStudents: number; feeNotSet: number;
}

export interface DashboardReport {
  range: ReportRange;
  options: { courses: string[]; mentors: MentorOption[] };
  students: {
    total: number; active: number; inactive: number; ranAway: number; instituteLeave: number;
    joined: number; joinedPrev: number | null; atRisk: number; idle: number;
    phases: { phase: string; count: number }[];
  };
  classes: ClassTally & {
    prev: ClassTally | null;
    today: { held: number; noShows: number };
    series: { unit: 'day' | 'week' | 'month'; points: ClassSeriesPoint[] };
  };
  fees: FeeTotals & { joined: { students: number; total: number; collected: number } | null };
  mentors: {
    mentorId: string; name: string; role: string; activeStudents: number;
    held: number; noShows: number; attPct: number | null; minutes: number; atRisk: number;
  }[];
  ranAwayByMentor?: RanAwayMentorRow[];   // missing from older servers
  enrollments: { key: string; label: string; count: number }[];
  unassignedLeads: number;
}

// Ran Away by Mentor — per mentor, the students given to them and how many
// ran away; then everyone who ran away (see getRanAwayReport in reports.js)
export interface RanAwayMentorRow {
  mentorId: string; name: string; role: string; status: string;
  students: number; ranAway: number; rate: number | null; active: number;
  early?: number; unpaid?: number;   // the Ran Away page only
}

export interface RanAwayStudent {
  studentId: string; name: string; phone: string; course: string; mode: '' | 'online' | 'offline';
  mentorId: string; mentorName: string; joined: string;
  attended: number; noShows: number; lastAttended: string; lastClass: string;
  courseFee: number; paid: number; unpaid: number; salesPerson: string; early: boolean;
}

export interface RanAwayReport {
  range: ReportRange;
  earlyClasses: number;
  options: { courses: string[] };
  totals: {
    students: number; ranAway: number; rate: number | null; early: number; never: number; unpaid: number;
    beforeMentor: number; beforeMentorUnpaid: number;
  };
  mentors: RanAwayMentorRow[];
  students: RanAwayStudent[];
  months: { key: string; label: string; joined: number; ranAway: number }[];
}

export async function getRanAwayReport(
  adminPassword: string,
  period: { from: string; to: string },
  filters: { course?: string } = {}
): Promise<{ ok: true } & RanAwayReport> {
  return withAdmin('getRanAwayReport', adminPassword, { ...period, ...filters });
}

export async function getAdminDashboardStats(
  adminPassword: string,
  period: ReportPeriod,
  filters: { course?: string; mentorId?: string } = {}
): Promise<{ ok: true } & DashboardReport> {
  return withAdmin('getAdminDashboardStats', adminPassword, { ...period, ...filters });
}

export interface MentorReportStudent {
  studentId: string; name: string; course: string; phase: string; status: string; joined: string;
  held: number; noShows: number; attPct: number | null;
  tasksDone: number; tasksMissed: number; tasksPending: number;
  lastAttended: string; daysIdle: number | null; atRisk: boolean; idle: boolean;
}

export interface MentorPerfRow {
  mentorId: string; name: string; role: string; status: string; activeStudents: number;
  period: ClassTally & { daysTaught: number; covered: number };
  prev: { held: number; noShows: number; attPct: number | null; minutes: number } | null;
  lastClass: string; atRiskCount: number; idleCount: number;
  students: MentorReportStudent[];
}

export async function getMentorPerformance(
  adminPassword: string,
  period: ReportPeriod,
  filters: { course?: string } = {}
): Promise<{ ok: true; range: ReportRange; options: { courses: string[] }; report: MentorPerfRow[] }> {
  return withAdmin('getMentorPerformance', adminPassword, { ...period, ...filters, includeInactive: true });
}

// Student Overview page — every active student's course progress, classes and
// tasks in one call. Each curriculum (with its day titles) comes once.
export interface OverviewCurriculum {
  key: string; title: string; totalDays: number;
  days: DayItem[]; projectCheckpoints: Checkpoint[];
}
export interface OverviewStudent {
  studentId: string; name: string; mentorId: string; mentorName: string;
  course: string; phase: CoursePhase; joined: string;
  curriculumKey: string;            // '' = no curriculum for this course
  completedDays: number[];          // sorted
  projectStatuses: Array<{ phase: number; status: ProjectStatus['status'] }>;
  portionsUpdated: string;          // YYYY-MM-DD the ticks last changed, '' = never
  held: number; noShows: number; attPct: number | null;
  tasksDone: number; tasksMissed: number; tasksPending: number;   // classes, by task status
  taskCount?: { given: number; done: number };                     // tasks, as the student portal counts them
  lastClass: { day: string; topic: string; present: boolean } | null;
  lastAttended: string; daysIdle: number | null; atRisk: boolean; idle: boolean;
}
export async function getStudentOverview(
  adminPassword: string
): Promise<{ ok: true; today: string; curricula: Record<string, OverviewCurriculum>; students: OverviewStudent[] }> {
  return withAdmin('getStudentOverview', adminPassword);
}

export interface StudentQuickStats {
  total: number; attended: number; attPct: number;
  taskTotal: number; taskDone: number; taskMiss: number; taskDonePct: number;
  recent: { date: string; topic: string; attendance: string } | null;
}
export async function getStudentQuickStats(studentId: string): Promise<{ ok: true; stats: StudentQuickStats }> {
  return post('getStudentQuickStats', { studentId });
}

export interface StudentFullDetail {
  student: {
    studentId: string; name: string; mentorId: string; mentorName: string;
    course: string; status: string; dateJoined: string; email: string;
    phone: string; salesPerson: string; phase: string; notes: string;
  };
  auth: { email: string; isFirstLogin: boolean } | null;
  stats: {
    totalLogs: number; attended: number; absent: number; attPct: number;
    taskTotal: number; taskDone: number; taskMiss: number; taskPending: number;
  };
  recentLogs: Array<{
    date: string; day: string; attendance: string;
    topic: string; completionStatus: string; minutes: number; takenBy: string;
  }>;
  progress: {
    curriculumKey: string; completedDays: number[];
    taskLogs: Array<{ date: string; tasksGiven: number; tasksCompleted: number }>;
  } | null;
  payment: {
    totalPaid: number; courseFee: number; sessionType: string; status: string; enrollDate: string;
  } | null;
}
export async function getStudentFullDetail(
  adminPassword: string,
  studentId: string
): Promise<{ ok: true } & StudentFullDetail> {
  return withAdmin('getStudentFullDetail', adminPassword, { studentId });
}

export type PayStatus = 'Fully Paid' | 'Partly Paid' | 'Fee not set' | 'Ran Away';

export interface RevenueRow {
  _id: string; name: string; email: string; phone: string; course: string; sessionType: string;
  mode: 'online' | 'offline'; enrollDate: string; joined: string;
  regPaid: number; balancePaid: number; courseFee: number; totalPaid: number; balance: number;
  payStatus: PayStatus; classOver: boolean; salesPerson: string; verificationStatus: string;
  studentId: string; studentStatus: string; mentorId: string; mentorName: string;
}
export async function getRevenueData(adminPassword: string): Promise<{ ok: true; today: string; rows: RevenueRow[] }> {
  return withAdmin('getRevenueData', adminPassword);
}

export interface JobApplicationRow {
  _id: string; jobId: string; studentId: string; studentName: string; studentEmail: string;
  course: string; interviewStatus: string; appliedAt: string; jobTitle: string; company: string;
}
export async function getAllJobApplications(adminPassword: string): Promise<{ ok: true; applications: JobApplicationRow[] }> {
  return withAdmin('getAllJobApplications', adminPassword);
}

export async function updateLead(
  adminPassword: string,
  leadId: string,
  data: { sessionType?: string; course?: string; salesPerson?: string; regPaid?: number }
): Promise<{ ok: true }> {
  return withAdmin('updateLead', adminPassword, { leadId, ...data } as unknown as Record<string, unknown>);
}

export async function addLead(
  adminPassword: string,
  data: { name: string; phone: string; email: string; course: string; sessionType: string; regPaid: number; courseFee: number; salesPerson: string; enrollDate: string }
): Promise<{ ok: true }> {
  return withAdmin('addLead', adminPassword, data as unknown as Record<string, unknown>);
}

export async function markLeadClassOver(
  adminPassword: string,
  id: string
): Promise<{ ok: true }> {
  return withAdmin('markLeadClassOver', adminPassword, { id });
}

export async function markLeadAlreadyAssigned(
  adminPassword: string,
  id: string
): Promise<{ ok: true }> {
  return withAdmin('markLeadAlreadyAssigned', adminPassword, { id });
}

// Older name for raiseClassOver (no note).
export async function mentorMarkClassOver(
  mentorId: string,
  studentId: string
): Promise<{ ok: true; studentName: string }> {
  return raiseClassOver(mentorId, studentId, '');
}

// ==================== PLACEMENT TRACKER API ==================

export interface PlacementStage {
  stageIndex: number;
  stageName: string;
  status: 'pending' | 'in_progress' | 'completed' | 'failed';
  completedDate: string;
  projectTitle: string;
  projectDetails: string;
  notes: string;
  companyName: string;
  interviewDate: string;
  offerStatus: '' | 'Offered' | 'Rejected' | 'Negotiating' | 'Accepted';
  salaryOffered: number;
}

// An application marked "Offer Received" on the Job Board
export interface PlacementJobOffer {
  jobId: string;
  jobTitle: string;
  company: string;
  salary: number;             // ₹ a year, 0 = not recorded
  at: string;
  byName: string;
}

export interface PlacementStudent {
  _id: string;
  studentId: string;
  studentName: string;
  course: string;
  mentorId: string;
  stages: PlacementStage[];
  studentStatus?: string;
  jobOffers?: PlacementJobOffer[];
  // Their tasks (server/routes/tasks.js): average of the last scores, how many reviewed / accepted / missed
  taskScore?: { avgScore: number | null; reviewed: number; done: number; given: number; onTimePct: number | null; missed: number } | null;
}

export async function getPlacementData(adminPassword: string): Promise<{ ok: true; students: PlacementStudent[]; inactiveStudents: PlacementStudent[] }> {
  return withAdmin('getPlacementData', adminPassword);
}

export async function addStudentToPlacement(adminPassword: string, studentId: string): Promise<{ ok: true }> {
  return withAdmin('addStudentToPlacement', adminPassword, { studentId });
}

export async function updatePlacementStage(
  adminPassword: string,
  studentId: string,
  stageIndex: number,
  update: { status?: string; completedDate?: string; projectTitle?: string; projectDetails?: string; notes?: string; companyName?: string; interviewDate?: string; offerStatus?: string; salaryOffered?: number }
): Promise<{ ok: true }> {
  return withAdmin('updatePlacementStage', adminPassword, { studentId, stageIndex, ...update });
}

export async function removeStudentFromPlacement(adminPassword: string, studentId: string): Promise<{ ok: true }> {
  return withAdmin('removeStudentFromPlacement', adminPassword, { studentId });
}

export interface DashboardFilters {
  mentorId?: string;
  studentId?: string;
  dateFrom?: string;
  dateTo?: string;
}

export async function getDashboard(adminPassword: string, filters: DashboardFilters = {}): Promise<DashboardData & { ok: true }> {
  return withAdmin('getDashboard', adminPassword, filters as Record<string, unknown>);
}

// ── COURSE PORTIONS ────────────────────────────────────────────────────────

export interface DayItem       { day: number; topic: string; }
export interface Checkpoint    { phase: number; afterDay: number; difficulty: string; description: string; }
export interface ProjectStatus { phase: number; status: 'locked' | 'available' | 'completed'; completedAt?: string; }
export interface PortionsData  {
  curriculumKey: string | null;
  curriculumTitle: string | null;
  totalDays: number | null;
  days: DayItem[];
  completedDays: number[];
  projectStatuses: ProjectStatus[];
  projectCheckpoints: Checkpoint[];
}

export async function getMentorStudentPortions(studentId: string): Promise<{ ok: true } & PortionsData> {
  return post('getMentorStudentPortions', { studentId });
}

export async function setMentorStudentPortions(studentId: string, completedDays: number[], mentorId: string): Promise<{ ok: true }> {
  return post('setMentorStudentPortions', { studentId, completedDays, mentorId });
}

export async function getStudentPortions(adminPassword: string, studentId: string): Promise<PortionsData> {
  return withAdmin('getStudentPortions', adminPassword, { studentId });
}

export async function setStudentPortions(adminPassword: string, studentId: string, completedDays: number[]): Promise<{ ok: true }> {
  return withAdmin('setStudentPortions', adminPassword, { studentId, completedDays });
}

export async function markProjectComplete(adminPassword: string, studentId: string, phase: number): Promise<{ ok: true; assessmentUnlocked: boolean }> {
  return withAdmin('markProjectComplete', adminPassword, { studentId, phase });
}

export async function assignCurriculum(adminPassword: string, studentId: string, curriculumKey: string): Promise<{ ok: true }> {
  return withAdmin('assignCurriculum', adminPassword, { studentId, curriculumKey });
}

// neverLoggedIn: the student has no password yet — nothing to reset; they log in with email + phone
export async function resetStudentPassword(adminPassword: string, studentId: string): Promise<{ ok: true; neverLoggedIn?: boolean }> {
  return withAdmin('resetStudentPassword', adminPassword, { studentId });
}

// ── COMPLAINTS ─────────────────────────────────────────────────────────────

export interface ComplaintUpdate { status: string; note: string; updatedBy: string; updatedAt: string; }
export interface ComplaintItem   {
  _id: string; studentId: string; studentName: string; studentEmail: string;
  title: string; description: string; status: string;
  history: ComplaintUpdate[]; createdAt: string;
}

export async function getAllComplaints(adminPassword: string, status?: string): Promise<{ ok: true; complaints: ComplaintItem[] }> {
  return withAdmin('getAllComplaints', adminPassword, status ? { status } : {});
}

export async function updateComplaintStatus(adminPassword: string, complaintId: string, status: string, note: string): Promise<{ ok: true }> {
  return withAdmin('updateComplaintStatus', adminPassword, { complaintId, status, note });
}

// ── JOB BOARD ──────────────────────────────────────────────────────────────
// One shared board (server/routes/jobBoard.js): placement coordinators see
// every job and anyone can edit, close, reopen or send a reminder for any of
// them; each of those is logged in job.activity.

export interface JobActivity {
  action: 'posted' | 'edited' | 'closed' | 'reopened' | 'reminder' | 'auto-closed';
  byId?: string;
  byName: string;
  at: string;
  detail?: string;
  emailed?: number;           // students emailed — the post's email or a reminder; one a day per job
}

export interface JobPosting {
  _id: string;
  title: string;
  company: string;
  description: string;
  jobLink: string;
  targetCourses: string[];
  postedBy: string;
  postedByName: string;
  status: 'Active' | 'Closed';
  createdAt: string;
  lastDate?: string;          // YYYY-MM-DD — last day to apply; the job closes itself the day after
  activity?: JobActivity[];
  applicationCount?: number;
}

export interface JobApplication {
  _id: string;
  studentId: string;
  studentName: string;
  studentEmail: string;
  course: string;
  createdAt: string;
  fileName?: string;
  screenshotBase64?: string;  // admin Job Board only
  hasScreenshot?: boolean;    // everyone else — fetch it with getApplicationScreenshot
  interviewStatus?: string;
  offerSalary?: number;       // ₹ a year, recorded with 'Offer Received'
  statusUpdatedByName?: string;
  statusUpdatedAt?: string;
}

export interface NotAppliedStudent {
  studentId: string;
  name: string;
  email: string;
  course: string;
}

export interface AdminJobEntry extends JobPosting {
  applications: JobApplication[];
  notAppliedStudents: NotAppliedStudent[];
  appliedCount: number;
  notAppliedCount: number;
  totalEligible: number;
  pct: number;
  threeDaysOld: boolean;
}

export async function setPlacementCoordinator(adminPassword: string, mentorId: string, isPlacementCoordinator: boolean): Promise<{ ok: true }> {
  return withAdmin('setPlacementCoordinator', adminPassword, { mentorId, isPlacementCoordinator });
}

export async function getMentorSlotConfig(mentorId: string): Promise<{ ok: boolean; slotConfig: object | null }> {
  return post('getMentorSlotConfig', { mentorId });
}

export async function setMentorSlotConfig(adminPassword: string, mentorId: string, slotConfig: object): Promise<{ ok: boolean }> {
  return withAdmin('setMentorSlotConfig', adminPassword, { mentorId, slotConfig });
}

export async function addJobPosting(
  mentorId: string,
  title: string,
  company: string,
  description: string,
  jobLink: string,
  targetCourses: string[],
  sendEmail = false,
  lastDate = '',
): Promise<{ ok: true; jobId: string; emailed: number }> {
  return post('addJobPosting', { mentorId, title, company, description, jobLink, targetCourses, sendEmail, lastDate });
}

// Every job, with applicants — screenshots are fetched one at a time
export async function getPlacementCoordinatorJobs(mentorId: string): Promise<{ ok: true; jobs: AdminJobEntry[] }> {
  return post('getPlacementCoordinatorJobs', { mentorId });
}

// Placement coordinators act with their mentorId; the admin with the password
// (a Student Coordinator's 'COORD:' session is translated by post())
export type JobBoardAuth = { mentorId: string } | { adminPassword: string };

export interface JobFields {
  title: string;
  company: string;
  description: string;
  jobLink: string;
  targetCourses: string[];
  lastDate: string;           // '' = no last date
}

export async function updateJob(who: JobBoardAuth, jobId: string, fields: Partial<JobFields>): Promise<{ ok: true; changed: string[] }> {
  return post('updateJobPosting', { ...who, jobId, ...fields });
}

export async function closeJob(who: JobBoardAuth, jobId: string): Promise<{ ok: true }> {
  return post('closeJobPosting', { ...who, jobId });
}

// lastDate replaces the job's last date ('' removes it) — needed once it has passed
export async function reopenJob(who: JobBoardAuth, jobId: string, lastDate?: string): Promise<{ ok: true }> {
  return post('reopenJobPosting', { ...who, jobId, ...(lastDate === undefined ? {} : { lastDate }) });
}

// Emails the eligible students who haven't applied — at most once a day per job
export async function sendJobReminder(who: JobBoardAuth, jobId: string): Promise<{ ok: true; sent: number }> {
  return post('sendJobReminder', { ...who, jobId });
}

export async function setApplicationStatus(
  who: JobBoardAuth, applicationId: string, interviewStatus: string, offerSalary?: number,
): Promise<{ ok: true; application: Pick<JobApplication, '_id' | 'interviewStatus' | 'offerSalary' | 'statusUpdatedByName' | 'statusUpdatedAt'> }> {
  return post('updateInterviewStatus', { ...who, applicationId, interviewStatus, ...(offerSalary === undefined ? {} : { offerSalary }) });
}

export async function getApplicationScreenshot(who: JobBoardAuth, applicationId: string): Promise<{ ok: true; screenshotBase64: string; fileName: string }> {
  return post('getJobApplicationScreenshot', { ...who, applicationId });
}

export async function getAdminJobBoard(adminPassword: string): Promise<{ ok: true; jobs: AdminJobEntry[] }> {
  return withAdmin('getAdminJobBoard', adminPassword);
}

export async function updateInterviewStatus(adminPassword: string, applicationId: string, interviewStatus: string): Promise<{ ok: true }> {
  return withAdmin('updateInterviewStatus', adminPassword, { applicationId, interviewStatus });
}

export async function closeJobPosting(adminPassword: string, jobId: string): Promise<{ ok: true }> {
  return withAdmin('closeJobPosting', adminPassword, { jobId });
}

export async function addJobPostingAdmin(
  adminPassword: string,
  title: string,
  company: string,
  description: string,
  jobLink: string,
  targetCourses: string[],
): Promise<{ ok: true; jobId: string }> {
  return withAdmin('addJobPostingAdmin', adminPassword, { title, company, description, jobLink, targetCourses });
}

// ── MENTOR DETAILS ──────────────────────────────────────────────────────────

export async function updateMentorDetails(
  adminPassword: string,
  mentorId: string,
  data: { email?: string; joiningDate?: string; birthday?: string; phone?: string }
): Promise<{ ok: true }> {
  return withAdmin('updateMentorDetails', adminPassword, { mentorId, ...data });
}

// ── TASKS ─────────────────────────────────────────────────────────────────────

export interface Task {
  taskId: string;
  title: string;
  description: string;
  assignedTo: string;
  assignedToName: string;
  assignedToEmail: string;
  assignedBy: string;
  assignedByName: string;
  priority: 'Low' | 'Medium' | 'High' | 'Urgent';
  dueDate: string;
  status: 'Pending' | 'In Progress' | 'Done' | 'Cancelled';
  completionNotes: string;
  completedAt: string | null;
  createdAt: string;
  attachments?: { name: string; mimeType: string; content?: string }[];
  history?: Array<{
    status: string;
    notes: string;
    changedAt: string;
    changedBy: string;
    changedByName: string;
  }>;
}

export interface TaskAttachment { name: string; content: string; type: string; }

export async function createTask(
  adminPassword: string,
  data: {
    title: string; description: string;
    assignedTo: string | string[];
    priority: string; dueDate: string;
    assignedByName: string; assignedBy: string;
    attachments?: TaskAttachment[];
  }
): Promise<{ ok: true; taskId: string; emailSent?: boolean; assignedCount?: number; emailsSent?: number }> {
  return withAdmin('createTask', adminPassword, data as unknown as Record<string, unknown>);
}

export async function getAllTasks(
  adminPassword: string,
  filters?: { mentorId?: string; status?: string }
): Promise<{ ok: true; tasks: Task[] }> {
  return withAdmin('getAllTasks', adminPassword, filters || {});
}

export async function getMyTasks(
  mentorId: string
): Promise<{ ok: true; tasks: Task[] }> {
  return post('getMyTasks', { mentorId });
}

export async function updateTaskStatus(
  mentorId: string,
  taskId: string,
  status: string,
  completionNotes?: string
): Promise<{ ok: true }> {
  return post('updateTaskStatus', { mentorId, taskId, status, completionNotes: completionNotes || '' });
}

export async function adminUpdateTask(
  adminPassword: string,
  taskId: string,
  updates: { title?: string; description?: string; priority?: string; dueDate?: string }
): Promise<{ ok: true }> {
  return withAdmin('adminUpdateTask', adminPassword, { taskId, ...updates });
}

export async function adminUpdateTaskStatus(
  adminPassword: string,
  taskId: string,
  status: string,
  completionNotes?: string
): Promise<{ ok: true }> {
  return withAdmin('updateTaskStatus', adminPassword, { taskId, status, completionNotes: completionNotes || '' });
}

export async function adminDeleteTask(
  adminPassword: string,
  taskId: string
): Promise<{ ok: true }> {
  return withAdmin('adminDeleteTask', adminPassword, { taskId });
}

export async function getPendingClassLogs(
  mentorId: string
): Promise<{ ok: true; pending: { studentId: string; studentName: string; date: string; dayName: string; slotStart: string }[] }> {
  return post('getPendingClassLogs', { mentorId });
}

export interface AttendanceMentor {
  mentorId: string;
  mentorName: string;
  status: 'Present' | 'Absent' | 'MentorLeave';
  scheduledCount: number;
  unloggedStudents: { studentId: string; studentName: string; slotStart: string }[];
  approvedLeave: { halfDay: boolean } | null;   // an approved leave request covers this day
}

// Someone on approved leave on the day (Admin → Leave Requests)
export interface StaffOnLeave {
  employeeType: PayslipEmployeeType;
  employeeId: string;
  name: string;
  role: string;        // mentor role, or 'Sales'
  halfDay: boolean;
}

export async function getMentorDayAttendance(
  adminPassword: string,
  date: string
): Promise<{ ok: true; date: string; dayName: string; instituteLeave: boolean; mentors: AttendanceMentor[]; staffOnLeave: StaffOnLeave[] }> {
  return withAdmin('getMentorDayAttendance', adminPassword, { date });
}

export async function setMentorAttendance(
  adminPassword: string,
  mentorId: string,
  date: string,
  status: 'Present' | 'Absent' | 'MentorLeave'
): Promise<{ ok: true }> {
  return withAdmin('setMentorAttendance', adminPassword, { mentorId, date, status });
}

export async function markStudentLeave(
  adminPassword: string,
  date: string,
  reason: string,
  mentorId?: string
): Promise<{ ok: true; count: number }> {
  return withAdmin('markInstituteLeave', adminPassword, { date, reason, ...(mentorId ? { mentorId } : {}) });
}

export async function markMentorLeave(
  adminPassword: string,
  mentorId: string,
  date: string
): Promise<{ ok: true; logged: number }> {
  return withAdmin('markMentorLeave', adminPassword, { mentorId, date });
}

export async function markInstituteLeave(
  adminPassword: string,
  date: string
): Promise<{ ok: true; logged: number }> {
  return withAdmin('markInstituteLeave', adminPassword, { date });
}

export async function cancelInstituteLeave(
  adminPassword: string,
  date: string
): Promise<{ ok: true }> {
  return withAdmin('cancelInstituteLeave', adminPassword, { date });
}

/* ── Analytics ──────────────────────────────────────────────────────────── */
export interface AnalyticsDay {
  dateStr: string;
  dayName: string;
  isWeekend: boolean;
  total: number;
  present: number;
  absent: number;
  leave: number;
}
export interface AnalyticsMentor {
  mentorId: string;
  mentorName: string;
  total: number;
  present: number;
  absent: number;
  leave: number;
}
export interface AttendanceAnalytics {
  ok: true;
  period: { from: string; to: string };
  totals: { total: number; present: number; absent: number; leave: number };
  byDay: AnalyticsDay[];
  byMentor: AnalyticsMentor[];
}
export async function getAttendanceAnalytics(
  adminPassword: string,
  opts?: { days?: number; filterMentorId?: string }
): Promise<AttendanceAnalytics> {
  return post('getAttendanceAnalytics', { adminPassword, ...opts });
}

/* ── Notices ────────────────────────────────────────────────────────────── */
export interface Notice {
  noticeId: string;
  title: string;
  content: string;
  priority: 'Info' | 'Important' | 'Urgent';
  createdBy: string;
  createdByName: string;
  targetAll: boolean;
  targetMentorIds: string[];
  pinned: boolean;
  expiresAt: string | null;
  readBy: string[];
  createdAt: string;
  isRead?: boolean;
}
export async function createNotice(
  adminPassword: string,
  data: {
    title: string;
    content: string;
    priority?: 'Info' | 'Important' | 'Urgent';
    targetAll?: boolean;
    targetMentorIds?: string[];
    pinned?: boolean;
    expiresAt?: string | null;
  }
): Promise<{ ok: true; noticeId: string }> {
  return post('createNotice', { adminPassword, ...data });
}
export async function getNotices(adminPassword: string): Promise<{ ok: true; notices: Notice[] }> {
  return post('getNotices', { adminPassword });
}
export async function deleteNotice(adminPassword: string, noticeId: string): Promise<{ ok: true }> {
  return post('deleteNotice', { adminPassword, noticeId });
}
export async function getMentorNotices(mentorId: string): Promise<{ ok: true; notices: (Notice & { isRead: boolean })[] }> {
  return post('getMentorNotices', { mentorId });
}
export async function markNoticeRead(mentorId: string, noticeId: string): Promise<{ ok: true }> {
  return post('markNoticeRead', { mentorId, noticeId });
}

// ── STUDENT TASKS AND REVIEWS (server/routes/tasks.js) ─────────────────────────
// Mentors send their mentorId; Student Coordinators and the admin their
// session's adminPassword ('COORD:<id>' becomes coordinatorId in post()).
export type StaffAuth = { mentorId: string } | { adminPassword: string };
export type TaskStatus = 'Given' | 'Submitted' | 'Accepted' | 'Redo' | 'Cancelled';
export interface TaskItem { title: string; details?: string; dueDate?: string }
export interface TaskFile { path: string; name: string; type: string; size: number }
export interface TaskTry { n: number; at: string; link: string; note: string; late: boolean; files: TaskFile[] }
export interface TaskCheck { at: string; byName: string; verdict: 'good' | 'needs'; note: string }
export interface TaskReviewEntry {
  n: number; at: string; byName: string; byId?: string; score: number; good: string; improve: string;
  result: 'Accepted' | 'Redo'; late: boolean; helpful: '' | 'yes' | 'no'; checks?: TaskCheck[];
}
export interface TaskDoubt { at: string; from: 'student' | 'mentor'; byName: string; text: string }
export interface StudentTaskRow {
  id: string; taskNo: number; studentId: string; studentName: string; course: string; givenByName: string;
  title: string; details: string; classDate: string; dueDate: string; dueLabel: string; status: TaskStatus; overdue: boolean;
  tries: TaskTry[]; reviews: TaskReviewEntry[]; doubts: TaskDoubt[];
  submittedAt: string | null; reviewDueAt: string | null; reviewLate: boolean; waitingMinutes: number;
  score: number | null; reviewedAt: string | null; createdAt: string; mentorName?: string;
}
export type TaskView = 'review' | 'waiting' | 'done' | 'all';
export interface TaskListData {
  view: TaskView; now: string; today: string; tasks: StudentTaskRow[];
  counts: { review: number; late: number; waiting: number; overdue: number; done: number };
  students: { studentId: string; name: string; mentorId: string; mentorName: string }[];
  canSeeTeam: boolean; seesAll: boolean;
}
export interface ReviewCheckMentor {
  id: string; name: string; reviews: number; onTimePct: number | null; avgScore: number | null; redoPct: number | null; redo: number;
  avgWords: number | null; shortPct: number; sameScore: number | null; copied: number; copiedStudents: number;
  helpfulYes: number; helpfulNo: number; lateNow: number; waiting: number; spot: { good: number; needs: number };
  flags: { key: string; text: string }[];
}
export interface ReviewCheckRow {
  taskId: string; taskNo: number; title: string; studentId: string; studentName: string; n: number; at: string; byId: string; byName: string;
  score: number; result: 'Accepted' | 'Redo'; good: string; improve: string; late: boolean; helpful: '' | 'yes' | 'no';
  words: number; short: boolean; copied: boolean; checks: TaskCheck[];
}
export interface ReviewCheckData { from: string; to: string; mentors: ReviewCheckMentor[]; reviews: ReviewCheckRow[]; totalReviews: number; lateNow: number }

export async function taskList(a: StaffAuth, f: { view: TaskView; scope?: 'mine' | 'team'; forMentor?: string; studentId?: string }): Promise<{ ok: true } & TaskListData> {
  return post('taskList', { ...a, ...f });
}
export async function taskGet(a: StaffAuth, taskId: string): Promise<{ ok: true; task: StudentTaskRow }> {
  return post('taskGet', { ...a, taskId });
}
export async function taskGive(a: StaffAuth, studentIds: string[], tasks: TaskItem[]): Promise<{ ok: true; given: number }> {
  return post('taskGive', { ...a, studentIds, tasks });
}
export async function taskUpdate(a: StaffAuth, taskId: string, changes: Partial<TaskItem> & { cancel?: boolean }): Promise<{ ok: true; task: StudentTaskRow }> {
  return post('taskUpdate', { ...a, taskId, ...changes });
}
export async function taskReview(a: StaffAuth, taskId: string, r: { score: number; good: string; improve: string; result: 'Accepted' | 'Redo' }): Promise<{ ok: true; task: StudentTaskRow }> {
  return post('taskReview', { ...a, taskId, ...r });
}
export async function taskAnswerDoubt(a: StaffAuth, taskId: string, text: string): Promise<{ ok: true; task: StudentTaskRow }> {
  return post('taskAnswerDoubt', { ...a, taskId, text });
}
// A signed link (a path under the API) to open a file the student sent
export async function taskFileLink(a: StaffAuth, taskId: string, path: string): Promise<{ ok: true; url: string; name: string; type: string }> {
  return post('taskFileLink', { ...a, taskId, path });
}
export const taskFileUrl = (path: string) => `${WEB_APP_URL}${path}`;
export async function reviewCheck(adminPassword: string, from = '', to = ''): Promise<{ ok: true } & ReviewCheckData> {
  return post('reviewCheck', { adminPassword, from, to });
}
export async function reviewSpotCheck(adminPassword: string, taskId: string, n: number, verdict: 'good' | 'needs', note = ''): Promise<{ ok: true; task: StudentTaskRow }> {
  return post('reviewSpotCheck', { adminPassword, taskId, n, verdict, note });
}
